import { faChildReaching } from "@fortawesome/pro-solid-svg-icons";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import { useMutation } from "urql";

import { InfiniteScrollGrid } from "@/components/list/InfiniteScrollGrid";
import { ContainerLoader } from "@/components/ui-v2/ContainerLoader/ContainerLoader";
import { useFilePickerState } from "@/components/ui-v2/FilePicker/state";
import { Icon } from "@/components/ui-v2/Icon/Icon";
import { StateCard } from "@/components/ui-v2/StateCard/StateCard";
import { DeleteDriveItems } from "@/features/drive/queries.graphql";
import {
  EmptyPageInfo,
  PageInfo,
  ResponseData,
  useInfinitePagination,
  useShowSnackbar,
  useUrqlResponseHandler,
} from "@/hooks";
import { getConnectionNodesTyped } from "@/utils/relay";

import {
  GetWorkspacePoseFiles,
  GetWorkspacePoseFilesQueryResult,
  GetWorkspacePoseFilesQueryVariables,
  PoseFileFragment,
} from "../../../../queries.graphql";
import { usePoserStore } from "../../../../state";
import { PoseMenuState, PoseViewData } from "../../../../types";
import { glbAttachmentName } from "../../../../utils";
import { PoseActionsMenu } from "../PoseActionsMenu/PoseActionsMenu";
import { PoseTile } from "../PoseTile/PoseTile";

interface WorkspacePosesProps {
  workspaceId: string;
  workspaceName: string;
}

export const WorkspacePoses = (props: WorkspacePosesProps) => {
  const { workspaceName, workspaceId } = props;

  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.up("md"));
  const isLarge = useMediaQuery(theme.breakpoints.up("lg"));

  const { getResponseFromState } = useUrqlResponseHandler();

  const { openPoseCreatorInEditMode, openPoseCreatorInReferencePoseMode } =
    usePoserStore();

  const { setSelectedFile, selectedFiles, setSelectionAsConfirmed } =
    useFilePickerState();

  const [poseMenuState, setPoseMenuState] = useState<PoseMenuState>({
    anchor: null,
    pose: null,
  });

  const { showSuccess } = useShowSnackbar();
  const [, deleteDriveItems] = useMutation(DeleteDriveItems);

  const queryArgs = useMemo(
    () => ({
      workspaceName,
      filtering: [
        {
          field: "sourceType",
          operator: "in",
          value: '["POSE"]',
        },
      ],
    }),
    [workspaceName],
  ) as GetWorkspacePoseFilesQueryVariables;

  const extractQueryItems = ({
    workspace,
  }: ResponseData<GetWorkspacePoseFilesQueryResult>): {
    pageInfo: PageInfo;
    items: PoseFileFragment[];
  } => {
    if (!workspace) {
      return {
        pageInfo: EmptyPageInfo,
        items: [],
      };
    }
    return {
      pageInfo: workspace.files.pageInfo as PageInfo,
      items: getConnectionNodesTyped(workspace.files),
    };
  };

  const variables = useMemo(() => ({ ...queryArgs, first: 10 }), [queryArgs]);

  const state = useInfinitePagination({
    query: GetWorkspacePoseFiles,
    variables,
    applyPagination: (vars, after) => ({ ...vars, after }),
    extractQueryItems,
  });

  const {
    items,
    isFetching,
    pageInfo: { hasNextPage: hasMorePages },
    fetchMore: fetchMoreItems,
    execute: reload,
  } = state;

  const handlePoseSelect = (poseFile: PoseFileFragment) => () => {
    setSelectedFile({
      id: poseFile.id,
      src: poseFile.url,
      name: poseFile.name,
      type: "pose",
      contentType: "image/png",
      thumbnailSrc: poseFile.thumbnails?.medium?.url || poseFile.url,
      interactedAt: new Date(),
      source: "pose",
    });
  };

  const handlePoseDoubleClick =
    (pose: PoseFileFragment, selected: boolean) => () => {
      if (!selected) {
        handlePoseSelect(pose)();
      }

      setSelectionAsConfirmed();
    };

  const handlePoseMenuOpen =
    (pose: PoseFileFragment) => (event: React.MouseEvent) => {
      const { x = 0, y = 0 } =
        (event?.target as HTMLElement)?.getBoundingClientRect() || {};
      setPoseMenuState({
        pose: {
          id: pose.id,
          modelUrl: pose.attachmentUrls?.[glbAttachmentName] ?? "",
          viewData: (pose.metadata?.poseData as PoseViewData) ?? {
            camera: { position: [0, 0, 0] },
            orbitControl: { target: [0, 0, 0] },
          },
        },
        anchor: {
          x: event.clientX || x,
          y: event.clientY || y,
        },
      });
    };

  const handlePoseMenuClose = () => {
    setPoseMenuState({
      pose: null,
      anchor: null,
    });
    reload();
  };

  const handleDuplicate = () => {
    if (!poseMenuState.pose) return;
    openPoseCreatorInReferencePoseMode(poseMenuState.pose);
    handlePoseMenuClose();
  };

  const handleEdit = () => {
    if (!poseMenuState.pose?.id) return;
    openPoseCreatorInEditMode(poseMenuState.pose.id);
    handlePoseMenuClose();
  };

  const handleDelete = async () => {
    if (!poseMenuState.pose?.id) return;
    const result = await deleteDriveItems({
      deleteDriveItemsInput: {
        workspaceId,
        fileIds: [poseMenuState.pose.id],
      },
    });
    const { error } = getResponseFromState({ state: result });

    if (!error) {
      reload();
      showSuccess("Pose successfully deleted");
      handlePoseMenuClose();
    }
  };

  const hasNoWorkspacePoses = !isFetching && items.length === 0;
  const hasWorkspacePoses = !isFetching && items.length > 0;

  const columnCount = useMemo(() => {
    if (isLarge) {
      return 8;
    }
    if (isMedium) {
      return 6;
    }
    return 4;
  }, [isMedium, isLarge]);

  return (
    <>
      {isFetching && <ContainerLoader size={80} />}
      {hasNoWorkspacePoses && (
        <StateCard
          title="Your workspace does not have saved poses yet"
          description="Create your first pose by clicking on 'Create Pose' above or by using a pose from the pose library as reference"
          icon={<Icon size="xl" icon={faChildReaching} />}
          fill
        />
      )}
      {hasWorkspacePoses && (
        <InfiniteScrollGrid<PoseFileFragment>
          items={items}
          ySpacing={40}
          widthPadding={0}
          columnCount={columnCount}
          renderItem={({ item, style }) => {
            if (!item) {
              return null;
            }

            const isSelected = selectedFiles.some(
              (file) => file.id === item.id,
            );

            return (
              <Box style={style} sx={{ pr: 2 }}>
                <PoseTile
                  key={item.id}
                  id={String(item.id)}
                  name={item.name}
                  selected={isSelected}
                  openPoseImageUrl={item.url}
                  onMenuOpen={handlePoseMenuOpen(item)}
                  onClick={handlePoseSelect(item)}
                  onDoubleClick={handlePoseDoubleClick(item, isSelected)}
                />
              </Box>
            );
          }}
          hasNextPage={hasMorePages}
          isNextPageLoading={isFetching}
          loadNextPage={fetchMoreItems}
        />
      )}
      <PoseActionsMenu
        open={!!poseMenuState.anchor}
        anchor={poseMenuState.anchor ?? undefined}
        onClose={handlePoseMenuClose}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onDuplicate={handleDuplicate}
      />
    </>
  );
};
